/* Groe — theme behaviour
   =======================================================================
   Four things move on this theme: the announcement bar dismiss, the sticky
   nav pill, the marquees (press + Instagram), and the two carousels.

   Everything is wrapped in an init(root) pass rather than running once at
   load. The Theme Editor re-renders a single section into the live DOM on
   every settings change and fires `shopify:section:load` on the new wrapper —
   markup wired up at page load is thrown away with the old node, so a
   carousel would go dead the moment a merchant edited it. Each initialiser
   is idempotent and marks what it has already claimed, so a re-run over a
   parent scope never double-binds or double-clones.
   ===================================================================== */
(function () {
  'use strict';

  /* Observers are torn down and rebuilt on re-init: they hold references to
     nodes that a section reload has already detached. */
  var observers = [];

  function claim(el, key) {
    if (!el || el.dataset[key] === 'true') return false;
    el.dataset[key] = 'true';
    return true;
  }

  /* ── announcement bar ─────────────────────────────────────── */
  function initAnnouncement(root) {
    var ann = root.querySelector('.ann');
    if (!claim(ann, 'groeAnn')) return;
    var close = ann.querySelector('.ann-x');
    if (close) close.addEventListener('click', function () { ann.hidden = true; });
  }

  /* ── sticky nav ───────────────────────────────────────────── */
  /* Read the pill's own position rather than a scroll threshold: it then stays
     correct whether or not the announcement bar has been dismissed, and however
     the fluid gutter and offsets resolve at this width.

     The sticky element is the SECTION WRAPPER, not .nav — see the comment in
     sections/header.liquid — so the offset is read off the wrapper. */
  function initNav(root) {
    var nav = root.querySelector('.nav');
    if (!claim(nav, 'groeNav')) return;

    var stuckTarget = nav.closest('.shopify-section') || nav;
    var stickTop = parseFloat(getComputedStyle(stuckTarget).top) || 0;
    var sync = function () {
      nav.classList.toggle('is-stuck', stuckTarget.getBoundingClientRect().top <= stickTop + 0.5);
    };
    addEventListener('scroll', sync, { passive: true });
    addEventListener('resize', sync);
    sync();
  }

  /* ── floating routine CTA ─────────────────────────────────── */
  /* Cross-section: it watches the hero CTA, the subscribe CTA and the footer,
     any of which may have just been re-rendered. Always rebuilt from document
     scope rather than from the reloaded section. */
  function initFloatingCta() {
    var sticky = document.getElementById('stickyRoutineCta');
    var heroCta = document.querySelector('.hero-cta');
    if (!sticky || !heroCta) return;

    var state = { hero: true, routine: false, footer: false };
    var sync = function () {
      sticky.classList.toggle('is-visible', !state.hero && !state.routine && !state.footer);
    };

    var watch = function (el, key) {
      if (!el) return;
      var io = new IntersectionObserver(function (entries) {
        state[key] = entries[0].isIntersecting;
        sync();
      }, { threshold: 0.15 });
      io.observe(el);
      observers.push(io);
    };

    watch(heroCta, 'hero');
    watch(document.getElementById('routineCta'), 'routine');
    watch(document.querySelector('.ftr'), 'footer');
    sync();
  }

  /* ── back to top ─────────────────────────────────────────── */
  /* Appears as soon as the viewport enters the lower half of the document,
     matching the client's "bottom half of the page" trigger. */
  function initBackToTop(root) {
    var button = root.querySelector('#backToTop');
    if (!claim(button, 'groeBackToTop')) return;

    var isVisible = false;
    var hideTimer;
    var reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

    function setVisible(next) {
      if (next === isVisible) return;
      isVisible = next;
      clearTimeout(hideTimer);

      if (next) {
        button.hidden = false;
        button.removeAttribute('tabindex');
        requestAnimationFrame(function () { button.classList.add('is-visible'); });
      } else {
        button.classList.remove('is-visible');
        button.setAttribute('tabindex', '-1');
        hideTimer = setTimeout(function () {
          if (!isVisible) button.hidden = true;
        }, reducedMotion.matches ? 0 : 180);
      }
    }

    function sync() {
      var pageHeight = document.documentElement.scrollHeight;
      setVisible(pageHeight > innerHeight && scrollY + innerHeight >= pageHeight * 0.5);
    }

    button.addEventListener('click', function () {
      scrollTo({ top: 0, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    });
    addEventListener('scroll', sync, { passive: true });
    addEventListener('resize', sync);
    sync();
  }

  /* ── seamless marquees ────────────────────────────────────── */
  /* The CSS translates each track by exactly one item-pitch, so only enough
     clones to cover the viewport plus that pitch are needed. The clones are
     aria-hidden: a screen reader should hear the list once. */
  function loop(track, copies) {
    if (!claim(track, 'groeLooped')) return;
    var originals = Array.prototype.slice.call(track.children);
    if (!originals.length) return;
    for (var i = 0; i < copies; i++) {
      originals.forEach(function (node) {
        var clone = node.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        track.appendChild(clone);
      });
    }
  }

  function initMarquees(root) {
    root.querySelectorAll('.ig-track').forEach(function (t) { loop(t, 2); });
  }

  /* ── testimonial carousel ─────────────────────────────────── */
  function initTestimonials(root) {
    var track = root.querySelector('#tstTrack');
    var rail = root.querySelector('#tstRail');
    var dots = root.querySelector('#tstDots');
    if (!track || !rail || !dots) return;
    if (!claim(rail, 'groeTst')) return;

    var cards = function () { return track.children.length; };
    if (!cards()) return;

    /* Start on the second review so the default state has a true centre card
       with a neighbour visible on each side. */
    var page = Math.min(1, cards() - 1);

    /* Card width and gap are fluid, so read them off the rendered card rather
       than hard-coding the 1440 values. offsetWidth is the untransformed layout
       width, so inactive-card scaling never shifts the active card off centre. */
    var gap = function () { return parseFloat(getComputedStyle(track).columnGap) || 0; };
    var step = function () { return track.firstElementChild.offsetWidth + gap(); };

    function render() {
      var total = cards();
      page = Math.min(Math.max(page, 0), total - 1);
      track.style.transform = 'translateX(' + (-page * step()) + 'px)';

      Array.prototype.forEach.call(track.children, function (card, i) {
        card.classList.toggle('is-active', i === page);
        /* A clip stops when its card slides out of the centre. */
        if (i !== page) card.querySelectorAll('video').forEach(function (v) { v.pause(); });
      });

      var buttons = [];
      for (var i = 0; i < total; i++) {
        buttons.push(dot(i));
      }
      dots.replaceChildren.apply(dots, buttons);
    }

    function dot(i) {
      var b = document.createElement('button');
      b.type = 'button';
      b.role = 'tab';
      b.setAttribute('aria-selected', String(i === page));
      b.setAttribute('aria-label', 'Testimonial page ' + (i + 1));
      b.addEventListener('click', function () { page = i; render(); });
      return b;
    }

    rail.querySelectorAll('.tst-arrow').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var total = cards();
        page = (page + Number(btn.dataset.dir) + total) % total;
        render();
      });
    });

    /* Older browsers without overflow:clip: undo any sideways scroll of the rail. */
    rail.addEventListener('scroll', function () { if (rail.scrollLeft) rail.scrollLeft = 0; });
    addEventListener('resize', render);
    render();
    initTestimonialVideos(track, function (i) { if (i !== page) { page = i; render(); } });
  }

  /* Optional testimonial clips: tap the play button to play with sound and
     native controls. Only one plays at a time; tapping a clip on a side card
     also brings that card to the centre. */
  function initTestimonialVideos(track, focus) {
    var videos = Array.prototype.slice.call(track.querySelectorAll('video.tst-video'));
    videos.forEach(function (v) {
      var box = v.parentElement;
      var btn = box.querySelector('.tst-play');
      btn.addEventListener('click', function () {
        focus(Array.prototype.indexOf.call(track.children, box.closest('.tst-card')));
        videos.forEach(function (o) { if (o !== v) o.pause(); });
        v.controls = true;
        var p = v.play();
        if (p && p.catch) p.catch(function () {});
      });
      v.addEventListener('play', function () { box.classList.add('is-playing'); });
      v.addEventListener('pause', function () {
        if (v.seeking) return;
        box.classList.remove('is-playing');
        v.controls = false;
      });
      v.addEventListener('ended', function () { v.currentTime = 0; v.load(); });
    });
  }

  /* ── advisor carousel ─────────────────────────────────────── */
  function initAdvisors(root) {
    var track = root.querySelector('#drTrack');
    var rail = root.querySelector('#drRail');
    var dots = root.querySelector('#drDots');
    if (!track || !rail || !dots) return;
    if (!claim(rail, 'groeDr')) return;

    var section = rail.closest('.dr') || rail;
    var arrows = section.querySelectorAll('.dr-arrow');

    var count = function () { return track.children.length; };
    if (!count()) return;

    var focus = Boolean(rail.closest('.dr-focus'));
    var page = focus ? Math.floor(count() / 2) : 0;

    function render() {
      var cards = Array.from(track.children);
      var max = count() - 1;
      page = Math.min(Math.max(page, 0), max);

      if (focus) {
        var live = cards[page];
        var offset = (rail.clientWidth / 2) - (live.offsetLeft + live.offsetWidth / 2);
        track.style.transform = 'translate3d(' + offset + 'px,0,0)';
      } else {
        track.style.transform = 'translateX(' + (-page * 100) + '%)';
      }

      cards.forEach(function (card, i) {
        card.toggleAttribute('data-live', i === page);
        card.setAttribute('aria-current', i === page ? 'true' : 'false');
      });

      var buttons = [];
      for (var i = 0; i < count(); i++) {
        buttons.push(dot(i));
      }
      dots.replaceChildren.apply(dots, buttons);

      arrows.forEach(function (btn) {
        var dir = Number(btn.dataset.dir);
        btn.disabled = !focus && ((dir < 0 && page === 0) || (dir > 0 && page === max));
        btn.style.opacity = btn.disabled ? '.35' : '1';
      });
    }

    function dot(i) {
      var b = document.createElement('button');
      b.type = 'button';
      b.role = 'tab';
      b.setAttribute('aria-selected', String(i === page));
      b.setAttribute('aria-label', 'Advisor ' + (i + 1));
      b.addEventListener('click', function () { page = i; render(); });
      return b;
    }

    arrows.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var next = page + Number(btn.dataset.dir);
        page = focus ? (next + count()) % count() : next;
        render();
      });
    });

    addEventListener('resize', render);
    render();
  }

  /* ── dispatch ─────────────────────────────────────────────── */
  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    initAnnouncement(scope);
    initNav(scope);
    initMarquees(scope);
    initTestimonials(scope);
    initAdvisors(scope);
    initBackToTop(scope);

    observers.forEach(function (io) { io.disconnect(); });
    observers = [];
    initFloatingCta();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:unload', function () { init(document); });
})();
